import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldCheck, LogIn, UserPlus, ArrowRight } from 'lucide-react';
import { useAuth, formatApiErrorDetail } from '../auth';
import { useLang } from '../i18n';
import api from '../api';
import { Card, Button, Field, Input, Spinner, Reveal, Badge } from '../components/ui';

const DEMOS = [
  { email: 'customer@tasco.vn', role: 'Customer' },
  { email: 'fleet@tasco.vn', role: 'Fleet Operator' },
  { email: 'adjuster@tasco.vn', role: 'Adjuster (Ops)' },
  { email: 'admin@tasco.vn', role: 'Admin' },
];

export default function Login() {
  const { login, register } = useAuth();
  const { t } = useLang();
  const nav = useNavigate();
  const [mode, setMode] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState('customer');
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);

  const routeFor = (u) => (u.role === 'admin' ? '/admin' : u.role === 'adjuster' ? '/ops' : u.role === 'fleet_operator' ? '/fleet' : '/carry');

  const submit = async (e) => {
    e?.preventDefault();
    setErr(''); setLoading(true);
    try {
      const u = mode === 'login' ? await login(email, password)
        : await register({ email, password, name: name || 'Customer', role });
      nav(routeFor(u));
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally { setLoading(false); }
  };

  const quick = async (em) => {
    setErr(''); setLoading(true);
    try { const u = await login(em, 'Tasco@123'); nav(routeFor(u)); }
    catch (e2) { setErr(formatApiErrorDetail(e2.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const [sent, setSent] = useState(false);
  const forgot = async (e) => {
    e?.preventDefault();
    setErr(''); setLoading(true);
    try { await api.post('/auth/forgot-password', { email }); setSent(true); }
    catch { setSent(true); }
    finally { setLoading(false); }
  };

  return (
    <div className="grain min-h-screen flex items-center justify-center px-5 py-10">
      <Reveal className="w-full max-w-md">
        <div className="flex items-center gap-2.5 justify-center mb-6">
          <div className="w-10 h-10 rounded-xl bg-jade/15 border border-jade/40 flex items-center justify-center">
            <ShieldCheck className="w-6 h-6 text-jade" />
          </div>
          <div className="font-display font-extrabold text-2xl">Tasco</div>
        </div>

        <Card className="p-7" data-testid="login-card">
          {mode === 'forgot' ? (
            <div data-testid="forgot-panel">
              <div className="font-display text-xl font-bold mb-1">Reset your password</div>
              {sent ? (
                <div className="mt-3" data-testid="forgot-sent">
                  <p className="text-slate-300 text-sm">If that email is registered, we've sent a password reset link. Check your inbox and follow the link to choose a new password.</p>
                  <Button data-testid="forgot-back" onClick={() => { setMode('login'); setSent(false); }} className="w-full mt-5">Back to sign in</Button>
                </div>
              ) : (
                <>
                  <p className="text-slate-400 text-sm mb-4">Enter your account email and we'll send you a reset link.</p>
                  <form onSubmit={forgot} className="space-y-4">
                    <Field label="Email"><Input data-testid="forgot-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" /></Field>
                    {err && <div className="text-crimson text-sm" data-testid="forgot-error">{err}</div>}
                    <Button type="submit" data-testid="forgot-submit" disabled={loading || !email} className="w-full">
                      {loading ? <Spinner label="…" /> : <>Send reset link <ArrowRight className="w-4 h-4" /></>}
                    </Button>
                  </form>
                  <div className="mt-4 text-center">
                    <button type="button" onClick={() => setMode('login')} data-testid="forgot-cancel" className="text-sm text-slate-400 hover:text-jade">Back to sign in</button>
                  </div>
                </>
              )}
            </div>
          ) : (
          <>
          <div className="flex gap-2 mb-5">
            {['login', 'register'].map((m) => (
              <button key={m} onClick={() => setMode(m)} data-testid={`auth-tab-${m}`}
                className={`flex-1 py-2 rounded-full text-sm font-medium ${mode === m ? 'bg-jade/10 text-jade border border-jade/40' : 'text-slate-400 border border-white/10'}`}>
                {m === 'login' ? 'Sign in' : 'Create account'}
              </button>
            ))}
          </div>

          <form onSubmit={submit} className="space-y-4">
            {mode === 'register' && (
              <>
                <Field label="Name"><Input data-testid="auth-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" /></Field>
                <Field label="Account type">
                  <div className="flex gap-2">
                    {['customer', 'fleet_operator'].map((r) => (
                      <button type="button" key={r} onClick={() => setRole(r)} data-testid={`auth-role-${r}`}
                        className={`flex-1 py-2.5 rounded-xl text-sm border ${role === r ? 'bg-jade/10 text-jade border-jade/40' : 'text-slate-400 border-white/10'}`}>
                        {r === 'customer' ? 'Customer' : 'Fleet Operator'}
                      </button>
                    ))}
                  </div>
                </Field>
              </>
            )}
            <Field label="Email"><Input data-testid="auth-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" /></Field>
            <Field label="Password"><Input data-testid="auth-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" /></Field>
            {mode === 'login' && (
              <div className="text-right -mt-1">
                <button type="button" onClick={() => { setErr(''); setMode('forgot'); }} data-testid="forgot-link" className="text-xs text-slate-400 hover:text-jade">Forgot password?</button>
              </div>
            )}
            {err && <div className="text-crimson text-sm" data-testid="auth-error">{err}</div>}
            <Button type="submit" data-testid="auth-submit" disabled={loading || !email || !password} className="w-full">
              {loading ? <Spinner label="…" /> : <>{mode === 'login' ? <LogIn className="w-4 h-4" /> : <UserPlus className="w-4 h-4" />} {mode === 'login' ? 'Sign in' : 'Create account'}</>}
            </Button>
          </form>

          <div className="mt-6 pt-5 border-t border-white/10">
            <div className="kicker mb-2">Demo accounts · password Tasco@123</div>
            <div className="grid grid-cols-2 gap-2">
              {DEMOS.map((d) => (
                <button key={d.email} onClick={() => quick(d.email)} data-testid={`demo-login-${d.role.split(' ')[0].toLowerCase()}`}
                  className="chip px-3 py-2 text-xs text-left text-slate-300 flex items-center justify-between">
                  <span>{d.role}</span><ArrowRight className="w-3 h-3" />
                </button>
              ))}
            </div>
          </div>
          </>
          )}
        </Card>
      </Reveal>
    </div>
  );
}
